import type { ReactNode } from 'react';
import {
  columnFilteringFeature,
  columnOrderingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFilteredRowModel,
  FilterFn,
  metaHelper,
  rowExpandingFeature,
  rowSortingFeature,
  tableFeatures,
} from '@tanstack/react-table';
import { LegacyColumnConfig } from './types';
import { parseCellValue } from './cell-renderers/parseCellValue';

// null/undefined is a genuine "no value" result, not a failed parse — never fall back to the raw
// column value for it. A non-primitive ReactNode can't be turned into a filterable scalar at all.
export const scalarParseObjectValue = (value: ReactNode): string | null => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : null;
};

// `ref: true` columns always render their {id, name} value wrapped in a Tooltip (JSX, not a
// string) — extract the same name-or-id text directly instead of trying to parse the render output.
const refScalarValue = (v: unknown): string | null => {
  if (typeof v !== 'object' || v === null) return null;
  const { id, name } = v as { id?: unknown; name?: unknown };
  if (typeof name === 'string' && name !== '') return name;
  if (id != null) return String(id);
  return null;
};

// Single source of truth for a column's filter key/label, shared by ClientFilterModal and
// ActiveFilterChips so the two can't drift apart into disagreeing on the same row's value.
export const buildColumnFilterOptions = (
  columnKey: string,
  config: LegacyColumnConfig,
  allRows: object[],
  noValueLabel: ReactNode
): Map<unknown, ReactNode> => {
  const vals = new Map<unknown, ReactNode>();
  allRows.forEach((row) => {
    const r = row as Record<string, unknown>;
    const v = r[columnKey];
    if (config.parseObject) {
      // parseObject reads the whole row, not columnKey — must run before any row[columnKey] check.
      const parsed = scalarParseObjectValue(config.parseObject(row));
      if (parsed !== null) {
        vals.set(parsed, parsed === '' ? noValueLabel : parsed);
        return;
      }
      // A rendered element rather than a scalar (e.g. an action button) — fall back to the raw value.
      if (v == null || v === '') return;
      const raw = String(v);
      vals.set(raw, raw);
      return;
    }
    if (v == null || v === '') {
      // Some columns render something other than blank for a missing value (e.g. "ongoing") —
      // use that label so the filter matches what the cell actually shows.
      const label = parseCellValue(v, config, row);
      const hasVisibleLabel = typeof label === 'string' || typeof label === 'number';
      vals.set('', hasVisibleLabel && label !== '' ? label : noValueLabel);
      return;
    }
    if (config.ref) {
      // A ref column's rendered label is JSX, not a string — see refScalarValue above.
      const refScalar = refScalarValue(v);
      vals.set(refScalar ?? String(v), refScalar ?? noValueLabel);
      return;
    }
    const label = parseCellValue(v, config, row);
    // Key on the raw value only if it's already scalar; otherwise use the rendered label's
    // scalar form (via scalarParseObjectValue, so a number/boolean label works too) — matching
    // exactly what multiValueFilter keys on below, so the two can't disagree on the same row.
    const isScalarValue = typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
    const key = isScalarValue ? v : (scalarParseObjectValue(label) ?? String(v));
    vals.set(key, label === '' ? noValueLabel : label);
  });
  return vals;
};

// Multi-value inclusion filter: column value must be in the selected array.
// Typed like TanStack's own built-in filterFn_* exports (FilterFn<any, any>) since a
// registry-slot filter function must stay assignable across every TFeatures/TData usage.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const multiValueFilter: FilterFn<any, any> = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  // meta's concrete shape is LegacyColumnConfig, erased by the FilterFn<any, any> registry typing.
  const meta = row.table.getColumn(columnId)?.columnDef.meta as LegacyColumnConfig | undefined;
  const rawValue = row.getValue(columnId);
  // Same priority/scalar-extraction order as buildColumnFilterOptions, so the two never disagree.
  const scalar = meta?.parseObject
    ? scalarParseObjectValue(meta.parseObject(row.original))
    : meta?.ref
      ? refScalarValue(rawValue)
      : (scalarParseObjectValue(rawValue as ReactNode) ??
        (meta ? scalarParseObjectValue(parseCellValue(rawValue, meta, row.original)) : null));
  const cellValue = scalar ?? String(rawValue ?? '');
  return filterValue.map(String).includes(cellValue);
};

export const cleosTableFeatures = tableFeatures({
  columnVisibilityFeature,
  columnOrderingFeature,
  columnFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  rowExpandingFeature,
  expandedRowModel: createExpandedRowModel(),
  rowSortingFeature,
  filterFns: { multiValue: multiValueFilter },
  columnMeta: metaHelper<LegacyColumnConfig & { columnKey: string }>(),
});

export type CleosTableFeaturesType = typeof cleosTableFeatures;
